import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { LatLng } from '~/_shared/types/latLng';
import {
  jtsParse, operations,
} from '~/_shared/types/polygon/jsts.utils';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import {
  convertLatLngToCoordinate, convertMultiPolygonToGeometry,
} from '~/_shared/types/polygon/polygon.utils';
import {
  isGroupRadius, isRadiusProximity, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { convertDistanceToUnitSystem } from '~/_shared/utils/distance/distance.helpers';
import { getGroupRadiusProximityDetails } from '~/proximity/proximity.helpers';

export const AGS_REPORT_MAX_RADIUS_IN_MILES = 75;

// simplified but solid contiguous US polygon (counter-clockwise, closed)
const US_POLYGON: MultiPolygon = [
  {
    path: [
      { lat: 22.3458859, lng: -160.6775854 },
      { lat: 20.951616, lng: -160.4704514 },
      { lat: 20.6850167, lng: -157.4373958 },
      { lat: 18.5355569, lng: -155.8062441 },
      { lat: 19.8456824, lng: -153.4868574 },
      { lat: 21.8833919, lng: -156.6226542 },
      { lat: 22.6541293, lng: -159.9386872 },
      { lat: 22.3458859, lng: -160.6775854 },
    ],
    holes: [],
  },
  {
    path: [
      { lat: 50.9149129, lng: -180.0 },
      { lat: 52.444083, lng: -177.9438356 },
      { lat: 53.0665832, lng: -171.5769012 },
      { lat: 54.5205949, lng: -167.4362524 },
      { lat: 55.4343098, lng: -163.2783197 },
      { lat: 57.6687398, lng: -158.0359084 },
      { lat: 58.6150559, lng: -157.5106701 },
      { lat: 58.2973274, lng: -159.0482121 },
      { lat: 58.5393508, lng: -159.5916694 },
      { lat: 58.4492243, lng: -162.3285993 },
      { lat: 59.7852976, lng: -162.3952954 },
      { lat: 59.8387399, lng: -164.4317088 },
      { lat: 59.623236, lng: -167.3586438 },
      { lat: 60.5106716, lng: -167.6991592 },
      { lat: 60.5415657, lng: -165.7463763 },
      { lat: 61.6313691, lng: -166.4225919 },
      { lat: 63.1693625, lng: -165.7267579 },
      { lat: 64.1000418, lng: -161.5606979 },
      { lat: 64.1456637, lng: -167.4911273 },
      { lat: 65.5441308, lng: -168.6898911 },
      { lat: 67.0119104, lng: -164.8180569 },
      { lat: 68.7309967, lng: -167.7234527 },
      { lat: 71.7059285, lng: -157.4136433 },
      { lat: 69.9365559, lng: -140.8484396 },
      { lat: 60.4435726, lng: -140.8455938 },
      { lat: 60.6001834, lng: -139.0245123 },
      { lat: 60.122591, lng: -138.7094093 },
      { lat: 59.3804084, lng: -137.3589461 },
      { lat: 59.9630907, lng: -135.1608459 },
      { lat: 56.8173502, lng: -131.4560815 },
      { lat: 56.2620546, lng: -130.0301622 },
      { lat: 55.3155331, lng: -129.6889272 },
      { lat: 54.5880765, lng: -130.5185009 },
      { lat: 54.5756769, lng: -131.7981713 },
      { lat: 54.5347039, lng: -132.9373398 },
      { lat: 56.6187988, lng: -135.5565658 },
      { lat: 58.9902586, lng: -138.4968127 },
      { lat: 60.0024261, lng: -142.6007826 },
      { lat: 59.8647603, lng: -144.1349555 },
      { lat: 59.2522225, lng: -146.1689631 },
      { lat: 59.6396179, lng: -149.2309107 },
      { lat: 58.6358812, lng: -150.7906648 },
      { lat: 57.5609295, lng: -150.6989384 },
      { lat: 55.17602, lng: -156.8788019 },
      { lat: 53.2873205, lng: -164.297345 },
      { lat: 50.8061006, lng: -178.1930586 },
      { lat: 52.6773803, lng: -180.0 },
      { lat: 50.9149129, lng: -180.0 },
    ],
    holes: [],
  },
  {
    path: [
      { lat: 52.6773803, lng: 180.0 },
      { lat: 53.0842198, lng: 171.4172074 },
      { lat: 53.0899735, lng: 171.4115697 },
      { lat: 53.5870343, lng: 171.9827686 },
      { lat: 51.2758912, lng: 174.0055794 },
      { lat: 53.0842198, lng: 171.4172074 },
      { lat: 50.9149129, lng: 180.0 },
      { lat: 52.6773803, lng: 180.0 },
    ],
    holes: [],
  },
  {
    path: [
      { lat: 57.4215409, lng: -171.6798372 },
      { lat: 57.4510504, lng: -171.7071661 },
      { lat: 56.9613874, lng: -167.3412069 },
      { lat: 55.6808587, lng: -169.8256722 },
      { lat: 57.4215409, lng: -171.6798372 },
    ],
    holes: [],
  },
  {
    path: [
      { lat: 59.9143229, lng: -172.5161038 },
      { lat: 60.9281617, lng: -173.7673325 },
      { lat: 60.9331888, lng: -173.7673325 },
      { lat: 60.5743592, lng: -171.3228075 },
      { lat: 59.9143229, lng: -172.5161038 },
    ],
    holes: [],
  },
  {
    path: [
      { lat: 63.0678363, lng: -172.1035687 },
      { lat: 63.8724165, lng: -172.2027424 },
      { lat: 63.9568411, lng: -170.9639414 },
      { lat: 63.3110747, lng: -167.7581828 },
      { lat: 62.6752152, lng: -169.1006051 },
      { lat: 63.0678363, lng: -172.1035687 },
    ],
    holes: [],
  },
  {
    path: [
      { lat: 48.50304328003876, lng: -124.87452206512503 },
      { lat: 48.55650318734243, lng: -124.51483439050641 },
      { lat: 48.33423176680986, lng: -123.4601468904064 },
      { lat: 49.04965506031465, lng: -123.36390194793753 },
      { lat: 49.1015701830057, lng: -95.34119874088542 },
      { lat: 49.50274294543563, lng: -95.25330811588542 },
      { lat: 49.48847156592028, lng: -94.74793702213542 },
      { lat: 48.85640638563625, lng: -94.57215577213542 },
      { lat: 48.21625854113776, lng: -89.45252686588542 },
      { lat: 48.5373442862389, lng: -88.41981202213542 },
      { lat: 46.19679159584113, lng: -83.07516338306819 },
      { lat: 45.84263947112383, lng: -83.42228612608068 },
      { lat: 45.3583842813609, lng: -82.35661229795568 },
      { lat: 43.598757618333956, lng: -81.99406346983068 },
      { lat: 42.385487930229935, lng: -82.51786436099503 },
      { lat: 42.13269566912496, lng: -82.94332744556819 },
      { lat: 41.838710903039754, lng: -82.54781963306819 },
      { lat: 42.958272253572886, lng: -79.20797588306819 },
      { lat: 43.629952836615644, lng: -79.42770244556819 },
      { lat: 43.85219714551542, lng: -78.50485088306819 },
      { lat: 43.868040168520956, lng: -76.92281963306819 },
      { lat: 45.26857636559324, lng: -74.94292234192801 },
      { lat: 45.20668660762954, lng: -71.73491452942801 },
      { lat: 47.67414579531776, lng: -69.16411374817801 },
      { lat: 47.496301253524884, lng: -68.08745359192801 },
      { lat: 47.064014424808846, lng: -67.53813718567801 },
      { lat: 45.80723658879821, lng: -67.62602781067801 },
      { lat: 45.66921679920694, lng: -67.42827390442801 },
      { lat: 44.8027600505904, lng: -66.68120359192801 },
      { lat: 42.800948675905495, lng: -70.26260529036362 },
      { lat: 41.27106768289916, lng: -69.33989499817801 },
      { lat: 41.006299743059365, lng: -69.82329343567801 },
      { lat: 40.3062057835516, lng: -73.69048093567801 },
      { lat: 36.89718667878677, lng: -75.65223320740681 },
      { lat: 35.15583773701895, lng: -75.16883476990681 },
      { lat: 31.541081576509733, lng: -80.72791680115681 },
      { lat: 30.14026773780311, lng: -81.05464554377461 },
      { lat: 26.58350201068728, lng: -79.62642288752461 },
      { lat: 24.90127044144673, lng: -79.95601273127461 },
      { lat: 24.041332264568023, lng: -82.21919632502461 },
      { lat: 24.64190970726456, lng: -83.51558304377461 },
      { lat: 25.080509527798615, lng: -83.31782913752461 },
      { lat: 25.199856757363275, lng: -81.53804398127461 },
      { lat: 28.222018864406003, lng: -83.64741898127461 },
      { lat: 28.608526288772865, lng: -83.42769241877461 },
      { lat: 29.434703742834902, lng: -83.97700882502461 },
      { lat: 29.154778596028933, lng: -85.50317825841861 },
      { lat: 29.652473209962963, lng: -88.49145950841861 },
      { lat: 29.02036874899972, lng: -88.60132278966861 },
      { lat: 28.480982993045174, lng: -89.83179153966861 },
      { lat: 29.42307078849658, lng: -93.80884232091861 },
      { lat: 27.413446171941462, lng: -97.03882278966861 },
      { lat: 25.8421467060033, lng: -96.84106888341861 },
      { lat: 25.686041311615533, lng: -97.6319951089023 },
      { lat: 26.52570631299828, lng: -99.48265600809864 },
      { lat: 29.28319301051302, lng: -101.25411911869155 },
      { lat: 29.667711340396416, lng: -102.28812620280121 },
      { lat: 28.76443707660445, lng: -103.14376755619155 },
      { lat: 29.496341328255212, lng: -104.70695568659067 },
      { lat: 30.500111215661754, lng: -105.09697033502817 },
      { lat: 31.6762636194757, lng: -106.53843673612673 },
      { lat: 31.68502682825892, lng: -108.12599317438857 },
      { lat: 31.239899477166837, lng: -108.12599317438857 },
      { lat: 31.22580840609725, lng: -111.11976758835107 },
      { lat: 32.50886521898257, lng: -115.19042624812943 },
      { lat: 32.32337722697789, lng: -117.45360984187943 },
      { lat: 32.43471578556801, lng: -118.59618796687943 },
      { lat: 34.2223953901941, lng: -121.05667219143739 },
      { lat: 40.14795133885448, lng: -124.82419536541138 },
      { lat: 41.27433015939367, lng: -124.43114883234543 },
      { lat: 42.88104230846487, lng: -125.06335681623685 },
      { lat: 44.82962320870189, lng: -124.47009509748685 },
      { lat: 46.54851577045382, lng: -124.40417712873685 },
      { lat: 48.0885679942807, lng: -124.96328189128181 },
      { lat: 48.35208137300707, lng: -125.03194644196306 },
      { lat: 48.50304328003876, lng: -124.87452206512503 },
    ],
    holes: [],
  },
];

const US_POLYGON_GEOMETRY = jtsParse(convertMultiPolygonToGeometry(US_POLYGON));

export const isPolygonWithinUS = (multiPolygon: MultiPolygon): boolean => {
  const polygonGeometry = jtsParse(convertMultiPolygonToGeometry(multiPolygon));

  return operations.relate.intersects(US_POLYGON_GEOMETRY, polygonGeometry);
};

export const isProximityCenterWithinUS = (center: LatLng): boolean => {
  const jstsPoint = jtsParse({
    type: 'Point',
    coordinates: convertLatLngToCoordinate(center),
  });

  return operations.relate.contains(
    US_POLYGON_GEOMETRY,
    jstsPoint,
  );
};

export const isProximityValidForReportExport = (proximity: Proximity, spreadsheetRowId?: SpreadsheetRowId) => {
  if (isRadiusProximity(proximity) || isGroupRadius(proximity)) {
    let distance = proximity.data.radius;
    let unit = proximity.data.unit;

    if (isGroupRadius(proximity)) {
      const groupRadiusProximityDetails = getGroupRadiusProximityDetails(proximity, spreadsheetRowId);
      distance = groupRadiusProximityDetails.radius;
      unit = groupRadiusProximityDetails.unit;
    }

    const radius = convertDistanceToUnitSystem({
      distance,
      currentUnitSystem: unit,
      targetUnitSystem: UnitSystem.imperial,
    });

    return radius <= AGS_REPORT_MAX_RADIUS_IN_MILES;
  }

  return true;
};
